const toStringOrNull = (value) => {
  if (value == null) return null;
  const normalized = String(value).trim();
  return normalized || null;
};

export const MESSENGER_V2_GROUP_INVITE_STATUS = Object.freeze({
  JOINABLE: 'joinable',
  ALREADY_MEMBER: 'already_member',
  EXPIRED: 'expired',
  GROUP_UNAVAILABLE: 'group_unavailable',
  GROUP_FULL: 'group_full',
});

export const toMessengerV2GroupInviteBooleanOrNull = (value) => {
  if (value === true || value === false) return value;
  if (typeof value !== 'string') return null;

  const normalized = value.trim().toLowerCase();
  if (normalized === 'true') return true;
  if (normalized === 'false') return false;
  return null;
};

export const toMessengerV2GroupInviteNonNegativeIntegerOrNull = (value) => {
  if (value == null || value === '') return null;
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return null;

  const normalized = Math.trunc(parsed);
  return normalized >= 0 ? normalized : null;
};

export const resolveMessengerV2GroupInviteStatus = (invite = null) =>
  toStringOrNull(
    invite?.status ?? invite?.groupInviteStatus ?? invite?.group_invite_status ?? null,
  );

export const resolveMessengerV2GroupInviteAvailability = (
  invite = null,
  { phase = 'ready', membershipFallback = null } = {},
) => {
  const status = resolveMessengerV2GroupInviteStatus(invite);
  const statusIsCanonical = phase === 'ready' && Boolean(status);
  const storedAlreadyMember =
    toMessengerV2GroupInviteBooleanOrNull(
      invite?.already_member ?? invite?.alreadyMember ?? null,
    ) === true;
  const canonicalAlreadyMember = status
    ? status === MESSENGER_V2_GROUP_INVITE_STATUS.ALREADY_MEMBER
    : storedAlreadyMember;
  const alreadyMember = statusIsCanonical
    ? canonicalAlreadyMember
    : membershipFallback != null
      ? Boolean(membershipFallback)
      : canonicalAlreadyMember;
  const pending = phase === 'loading';
  const expired =
    status === MESSENGER_V2_GROUP_INVITE_STATUS.EXPIRED ||
    toMessengerV2GroupInviteBooleanOrNull(
      invite?.expired ?? invite?.groupInviteExpired ?? invite?.group_invite_expired ?? null,
    ) === true;
  const full = status === MESSENGER_V2_GROUP_INVITE_STATUS.GROUP_FULL;
  const unavailable =
    phase === 'error' || status === MESSENGER_V2_GROUP_INVITE_STATUS.GROUP_UNAVAILABLE;
  const explicitJoinable =
    toMessengerV2GroupInviteBooleanOrNull(
      invite?.joinable ?? invite?.groupInviteJoinable ?? invite?.group_invite_joinable ?? null,
    ) === true;
  const joinable =
    !pending &&
    (alreadyMember ||
      (!expired &&
        !full &&
        !unavailable &&
        (status ? status === MESSENGER_V2_GROUP_INVITE_STATUS.JOINABLE : explicitJoinable)));

  return {
    status,
    alreadyMember,
    pending,
    expired,
    full,
    unavailable,
    joinable,
  };
};

export const isMessengerV2GroupInvitePreviewPayload = (preview = null) =>
  preview &&
  typeof preview === 'object' &&
  toStringOrNull(preview?.type ?? preview?.kind ?? null) === 'group_invite';

export const resolveMessengerV2GroupInviteName = (preview = null) =>
  toStringOrNull(preview?.groupName ?? preview?.group_name ?? null) ||
  toStringOrNull(preview?.title ?? null) ||
  null;

export const resolveMessengerV2GroupInviteSummaryText = (preview = null, fallback = null) => {
  if (!isMessengerV2GroupInvitePreviewPayload(preview)) {
    return toStringOrNull(fallback);
  }

  const groupName = resolveMessengerV2GroupInviteName(preview);
  if (groupName) {
    return `Join ${groupName}`;
  }

  return (
    toStringOrNull(preview?.description ?? null) ||
    toStringOrNull(preview?.siteName ?? preview?.site_name ?? null) ||
    toStringOrNull(fallback) ||
    'Group chat invite'
  );
};
